import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// The standing at-risk list on the board (the director's backlog, 7 Oct 2026:
// "nothing tells you on Wednesday that Tuesday's commit put three orders at
// risk"). The button is always there and says how things stand; the panel
// lists every finding with why and what to do. Worked out fresh by the server
// (GET /risks) from the open orders, the board and the conflict engine -- it
// follows the board, at most every 30 seconds, and whenever it is opened.

const RED = '#ef4444', AMBER = '#f59e0b', TEAL = '#14b8a6', DIM = '#9ca3af', MUTED = '#6b7280';
const LEVELS = [
  ['critical', 'Will miss a delivery or cannot be made', RED],
  ['warning', 'Needs a look', AMBER],
  ['note', 'Worth knowing', '#60a5fa'],
];
const KIND_ICON = { late: 'fa-truck-fast', short: 'fa-boxes-stacked', not_on_board: 'fa-calendar-xmark', overdue: 'fa-clock', grace: 'fa-hourglass-half',
  run: 'fa-triangle-exclamation', not_closed: 'fa-flag-checkered', held: 'fa-lock', on_hold: 'fa-pause' };

export default function AtRiskPanel({ refreshKey, onShow }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const last = useRef(0);
  const { isDirector } = useAuth();
  const [dismissing, setDismissing] = useState(null);   // { key, how, note } while choosing
  const [busyKey, setBusyKey] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback((force) => {
    if (!force && Date.now() - last.current < 30000) return;
    last.current = Date.now();
    api.get('/api/v1/risks')
      .then(r => { setData(r.data); setError(''); })
      .catch(err => setError(err.response?.data?.error || 'The at-risk list could not be worked out just now.'));
  }, []);
  useEffect(() => { load(false); }, [load, refreshKey]);

  // "I read it, I checked it ... I am ok with it" / "... I sorted it" (19 Oct 2026).
  async function dismiss(item) {
    setBusyKey(item.key); setActionError('');
    try {
      await api.post('/api/v1/risks/dismiss', { key: item.key, fp: item.fp, how: dismissing.how, note: dismissing.note || null });
      setDismissing(null);
      load(true);
    } catch (err) {
      setActionError(err.response?.data?.error || 'It could not be dismissed just now.');
      load(true);
    } finally { setBusyKey(null); }
  }
  async function restore(item) {
    setBusyKey(item.key); setActionError('');
    try { await api.post('/api/v1/risks/restore', { id: item.dismissal.id }); load(true); }
    catch (err) { setActionError(err.response?.data?.error || 'It could not be brought back just now.'); }
    finally { setBusyKey(null); }
  }
  const whenSaid = (iso) => new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const HOW = { ok: 'checked, OK as it is', sorted: 'sorted' };
  // People findings are usually sorted; the rest usually checked and accepted.
  const usualHow = (i) => (i.rule === 'staffing' || i.rule === 'mould_change_limit' ? 'sorted' : 'ok');
  useEffect(() => { if (open) load(true); }, [open, load]);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open]);

  const c = (data && data.count) || { critical: 0, warning: 0, note: 0 };
  const tone = c.critical ? RED : c.warning ? AMBER : MUTED;
  const label = !data ? (error ? 'At risk: ?' : 'At risk…')
    : c.critical ? `${c.critical} at risk` : c.warning ? `${c.warning} to check` : 'Nothing at risk';
  const tip = data ? `${c.critical} will miss a delivery or cannot be made · ${c.warning} need a look · ${c.note} worth knowing` : error || 'Working it out';

  return (
    <>
      <button onClick={() => setOpen(true)} title={tip} aria-label={`At risk: ${tip}`}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'system-ui', flexShrink: 0,
                 fontSize: 12, fontWeight: 700, color: tone, background: c.critical || c.warning ? `${tone}1a` : 'transparent', border: `1px solid ${c.critical || c.warning ? tone + '66' : '#2a3040'}` }}>
        <i className={`fa-solid ${c.critical || c.warning ? 'fa-triangle-exclamation' : 'fa-shield-halved'}`} style={{ fontSize: 11 }} />
        {label}
        {c.critical > 0 && c.warning > 0 && <span style={{ color: AMBER, fontWeight: 600 }}>· {c.warning} to check</span>}
      </button>

      {open && (
        <div role="dialog" aria-label="At risk on the board" style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.45)' }} onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(440px, 100%)', background: '#161b22', borderLeft: '1px solid #2a3040', boxShadow: '-12px 0 40px rgba(0,0,0,0.5)',
                     display: 'flex', flexDirection: 'column', fontFamily: 'system-ui', color: '#f0f2f5' }}>
            <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid #2a3040', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>At risk on the board</div>
                <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>
                  Worked out {data ? `at ${new Date(data.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'now'} from the open orders and the board; it changes as the board does.
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', color: DIM, fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '10px 14px 24px', flex: 1 }}>
              {error && <div style={{ fontSize: 13, color: AMBER, margin: '6px 4px 10px' }}>{error}</div>}
              {actionError && <div style={{ fontSize: 13, color: AMBER, margin: '6px 4px 10px' }}>{actionError}</div>}
              {data && !data.items.length && (
                <div style={{ fontSize: 13.5, color: DIM, padding: '18px 6px', lineHeight: 1.5 }}>
                  <b style={{ color: '#f0f2f5' }}>Nothing at risk.</b> Every open order is covered on the board before its cut-off, and no run where it stands breaks a rule the board checks.
                </div>
              )}
              {data && LEVELS.map(([lvl, title, colour]) => {
                const items = data.items.filter(i => i.level === lvl);
                if (!items.length) return null;
                return (
                  <section key={lvl} style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: colour, margin: '4px 4px 8px' }}>{title} · {items.length}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {items.map(i => (
                        <div key={i.key} style={{ background: '#1e2329', border: '1px solid #2a3040', borderLeft: `3px solid ${colour}`, borderRadius: 9, padding: '10px 12px' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                            <i className={`fa-solid ${KIND_ICON[i.kind] || 'fa-circle-info'}`} style={{ color: colour, fontSize: 12, width: 14, flexShrink: 0 }} aria-hidden="true" />
                            <div style={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.35 }}>{i.title}</div>
                          </div>
                          <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.5, margin: '5px 0 0 22px' }}>{i.says}</div>
                          {i.changed_since && (
                            <div style={{ fontSize: 11.5, color: AMBER, margin: '6px 0 0 22px' }}>
                              Back on the list: it changed since {i.changed_since.by || 'it'} dismissed it on {whenSaid(i.changed_since.at)} ({HOW[i.changed_since.how]}).
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0 0 22px' }}>
                            {i.at && onShow && (
                              <button onClick={() => { setOpen(false); onShow(i); }}
                                style={{ background: 'rgba(20,184,166,0.12)', border: `1px solid ${TEAL}66`, color: TEAL, borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Show on the board
                              </button>
                            )}
                            {(i.links || []).map(l => (
                              <button key={l.to} onClick={() => { setOpen(false); navigate(l.to); }}
                                style={{ background: 'none', border: '1px solid #2a3040', color: '#f0f2f5', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                {l.label}
                              </button>
                            ))}
                            {isDirector() && dismissing?.key !== i.key && (
                              <button onClick={() => setDismissing({ key: i.key, how: usualHow(i), note: '' })} title="Take it off the list: you have read it and checked it"
                                style={{ background: 'none', border: '1px solid #2a3040', color: DIM, borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>
                                <i className="fa-solid fa-check" style={{ fontSize: 10, marginRight: 5 }} />Dismiss
                              </button>
                            )}
                          </div>
                          {dismissing?.key === i.key && (
                            <div style={{ margin: '10px 0 0 22px', padding: '10px', border: '1px solid #2a3040', borderRadius: 8, background: '#161b22', display: 'flex', flexDirection: 'column', gap: 7 }}>
                              <div style={{ fontSize: 12, color: DIM }}>Off the list for as long as it says the same; it comes back if the board changes it.</div>
                              {[['ok', "Checked — I'm OK with it", 'You read it and checked it, and it can stay as it is.'], ['sorted', 'I sorted it', 'You dealt with it outside the board (people, material...).']].map(([v, t, sub]) => (
                                <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer', color: '#f0f2f5' }}>
                                  <input type="radio" checked={dismissing.how === v} onChange={() => setDismissing(d => ({ ...d, how: v }))} style={{ marginTop: 3 }} />
                                  <span>{t}<span style={{ display: 'block', fontSize: 11, color: MUTED }}>{sub}</span></span>
                                </label>
                              ))}
                              <input value={dismissing.note} onChange={e => setDismissing(d => ({ ...d, note: e.target.value }))} maxLength={300}
                                placeholder={dismissing.how === 'sorted' ? 'How, if worth saying: e.g. Ana stays longer' : 'Why, if worth saying'}
                                style={{ background: '#0f1117', border: '1px solid #374151', borderRadius: 6, color: '#f0f2f5', fontSize: 12, padding: '5px 8px' }} />
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button onClick={() => setDismissing(null)} style={{ background: 'none', border: '1px solid #374151', color: DIM, borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                                <button onClick={() => dismiss(i)} disabled={busyKey === i.key}
                                  style={{ background: 'rgba(20,184,166,0.15)', border: `1px solid ${TEAL}88`, color: TEAL, borderRadius: 7, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                  {busyKey === i.key ? 'Dismissing…' : 'Dismiss'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
              {data && (data.dismissed || []).length > 0 && (
                <section style={{ marginTop: 18 }}>
                  <button onClick={() => setShowDismissed(v => !v)}
                    style={{ background: 'none', border: 'none', color: MUTED, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className={`fa-solid fa-chevron-${showDismissed ? 'down' : 'right'}`} style={{ fontSize: 9 }} />Dismissed · {data.dismissed.length}
                  </button>
                  {showDismissed && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                      {data.dismissed.map(i => (
                        <div key={i.key} style={{ background: '#1a1f26', border: '1px solid #2a3040', borderRadius: 9, padding: '9px 12px', opacity: 0.85 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: DIM, lineHeight: 1.35 }}>{i.title}</div>
                          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>
                            {i.dismissal.by || 'Dismissed'} · {whenSaid(i.dismissal.at)} · {HOW[i.dismissal.how]}{i.dismissal.note ? ` — ${i.dismissal.note}` : ''}
                          </div>
                          {isDirector() && (
                            <button onClick={() => restore(i)} disabled={busyKey === i.key}
                              style={{ marginTop: 6, background: 'none', border: '1px solid #2a3040', color: DIM, borderRadius: 7, padding: '3px 9px', fontSize: 11.5, cursor: 'pointer' }}>
                              {busyKey === i.key ? 'Bringing it back…' : 'Bring it back'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
